import { useState, useCallback } from 'react';

const TOKEN_KEY = 'ib_admin_token';

function parseToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

export function useAdminAuth() {
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t && parseToken(t) ? t : null;
  });
  const [admin, setAdmin] = useState(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? parseToken(t) : null;
  });

  const login = useCallback(async (email, password) => {
    let r;
    try {
      r = await fetch('/.netlify/functions/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      throw new Error('Network error — the server could not be reached. Are you running netlify dev?');
    }

    let data;
    try {
      data = await r.json();
    } catch {
      const text = await r.text().catch(() => '');
      throw new Error(
        `Server returned non-JSON (status ${r.status}). ` +
        (text.includes('<!DOCTYPE') ? 'The /api/login route is not reaching the Netlify function — check that you are using "npm run dev" locally or that env vars are set on Netlify.' : text.slice(0, 200))
      );
    }

    if (!r.ok) throw new Error(data.error || `Login failed (${r.status})`);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setAdmin(data.admin);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  return {
    token,
    admin,
    login,
    logout,
    isAuthenticated: !!token,
  };
}
